import { SquarePlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { InstallGuideSheet } from './InstallGuideSheet';
import { isIosDevice } from './installHelp';

/** The browser's install prompt event (Chrome/Edge/Android). Not in lib.dom yet, so typed locally. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true;

/**
 * "Add to Home Screen" (#213/#218). On Chrome/Edge/Android we capture the browser's install prompt and
 * trigger it on click. On iOS there is no prompt event, so we open a guide with the Add-to-Home-Screen
 * steps for the specific iOS browser (Safari/Chrome/Edge/Firefox). Renders a confirmation once installed.
 */
export function InstallButton() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (installed) {
    return <p className="good-text small">ClassGuardian is installed on this device.</p>;
  }

  const ios = isIosDevice();

  // iOS: no install prompt exists, so open the per-browser Add-to-Home-Screen guide.
  if (ios) {
    return (
      <div className="stack">
        <button className="btn" onClick={() => setGuideOpen(true)}>
          <SquarePlus size={16} /> Add to Home Screen
        </button>
        {guideOpen && <InstallGuideSheet onClose={() => setGuideOpen(false)} />}
      </div>
    );
  }

  // A non-iOS browser that offers no install prompt (already installed, or no PWA support): just explain.
  if (!deferred) {
    return (
      <p className="muted small">
        Use your browser’s menu and choose <strong>Install ClassGuardian</strong> or <strong>Add to Home Screen</strong>. If you don’t
        see it, the app may already be installed.
      </p>
    );
  }

  return (
    <button
      className="btn"
      onClick={async () => {
        await deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice.outcome === 'accepted') setInstalled(true);
        setDeferred(null);
      }}
    >
      <SquarePlus size={16} /> Add to Home Screen
    </button>
  );
}
